import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users, MessageSquarePlus, Hash, User as UserIcon, Archive, ChevronLeft, MessageSquare, Megaphone, Radio, Wand2, Pin, Bookmark, Search, Bell } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { useCommsUnread } from "@/context/CommsUnreadContext";
import { registerCommandProvider } from "@/components/CommandPalette";
import ChannelList from "@/components/comms/ChannelList";
import MessageStream from "@/components/comms/MessageStream";
import Composer from "@/components/comms/Composer";
import NewChannelDialog from "@/components/comms/NewChannelDialog";
import NewDMDialog from "@/components/comms/NewDMDialog";
import ChannelDetailDrawer from "@/components/comms/ChannelDetailDrawer";
import BroadcastsDrawer from "@/components/comms/BroadcastsDrawer";
import DirectoryDrawer from "@/components/comms/DirectoryDrawer";
import DirectoryRail from "@/components/comms/DirectoryRail";
import ThreadDrawer from "@/components/comms/ThreadDrawer";
import PinsSavedDrawer from "@/components/comms/PinsSavedDrawer";
import SearchDrawer from "@/components/comms/SearchDrawer";
import KeywordAlertsDialog from "@/components/comms/KeywordAlertsDialog";
import ModuleMark from "@/components/identity/ModuleMark";
import { Skeleton } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

/**
 * Comms page — dense split-pane. Left rail lists channels + DMs; right pane
 * shows the active conversation's stream + composer.
 *
 * Route-driven active conversation via `?open=<conversation_id>`. On first
 * load we default to the first channel we can read.
 */
export default function Comms() {
  const { companyId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { has, entitlements } = useCompany();
  const { byConv, markRead, refresh: refreshUnread } = useCommsUnread();

  const canPost = has("comms.post");
  const canManage = has("comms.manage");
  const moduleOn = !!entitlements?.modules?.comms;

  const [channels, setChannels] = React.useState([]);
  const [dms, setDms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeConv, setActiveConv] = React.useState(null);   // full conversation doc
  const [newChannelOpen, setNewChannelOpen] = React.useState(false);
  const [newDMOpen, setNewDMOpen] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [broadcastsOpen, setBroadcastsOpen] = React.useState(false);
  const [directoryOpen, setDirectoryOpen] = React.useState(false);
  const [threadOpen, setThreadOpen] = React.useState(false);
  const [threadRootId, setThreadRootId] = React.useState(null);
  const [quoteTarget, setQuoteTarget] = React.useState(null);
  const [pinsOpen, setPinsOpen] = React.useState(false);
  const [pinsTab, setPinsTab] = React.useState("pins");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [keywordOpen, setKeywordOpen] = React.useState(false);
  const [sites, setSites] = React.useState([]);

  // Load sites once for the broadcast composer (site-scoped audience).
  React.useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/sites`).then(({ data }) => setSites(data.sites || [])).catch(() => setSites([]));
  }, [companyId]);

  const activeId = params.get("open") || null;

  const loadRail = React.useCallback(async () => {
    if (!companyId || !moduleOn) { setLoading(false); return; }
    setLoading(true);
    try {
      const [ch, dm] = await Promise.all([
        api.get(`/companies/${companyId}/comms/channels`),
        api.get(`/companies/${companyId}/comms/dms`),
      ]);
      setChannels(ch.data.channels || []);
      setDms(dm.data.dms || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load conversations");
    } finally {
      setLoading(false);
    }
  }, [companyId, moduleOn]);

  React.useEffect(() => { loadRail(); }, [loadRail]);

  // Register a Cmd+K provider that jumps to any channel or DM the user
  // can reach in the current company. Auto-cleanup on unmount.
  React.useEffect(() => {
    if (!moduleOn) return () => {};
    const unregister = registerCommandProvider(() => {
      const chItems = (channels || []).map((c) => ({
        id: `comms-ch-${c.id}`,
        section: "Comms",
        label: `#${c.slug || c.name}`,
        hint: c.description || undefined,
        onSelect: () => nav(`/c/${companyId}/m/comms?open=${c.id}`),
      }));
      const dmItems = (dms || []).map((c) => ({
        id: `comms-dm-${c.id}`,
        section: "Comms",
        label: `DM · ${c.counterpart?.name || "Direct message"}`,
        hint: c.counterpart?.email,
        onSelect: () => nav(`/c/${companyId}/m/comms?open=${c.id}`),
      }));
      return [...chItems, ...dmItems];
    });
    return unregister;
  }, [channels, dms, companyId, moduleOn, nav]);

  // Default to first channel when nothing selected.
  React.useEffect(() => {
    if (loading) return;
    if (activeId) return;
    const first = channels[0] || dms[0];
    if (first) setParams({ open: first.id }, { replace: true });
  }, [loading, activeId, channels, dms, setParams]);

  // Load the active conversation
  React.useEffect(() => {
    if (!activeId || !companyId) { setActiveConv(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/comms/conversations/${activeId}`);
        if (!cancelled) setActiveConv(data);
      } catch (e) {
        if (!cancelled) setActiveConv(null);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not open conversation");
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, companyId]);

  // Mark the active conversation as read on open + when new messages arrive
  // (the MessageStream calls onSeen for each fresh message).
  React.useEffect(() => {
    if (activeId) markRead(activeId, null);
  }, [activeId, markRead]);

  // Live: react to SSE comms events at the rail level (bump last_message summary).
  React.useEffect(() => {
    const onEvt = (e) => {
      const evt = e.detail;
      if (!evt || evt.module !== "comms") return;
      if (evt.type === "conversation.created") {
        loadRail();
        return;
      }
      if (evt.type === "message.created") {
        const { conversation_id, message, conversation_kind } = evt.payload || {};
        if (!conversation_id || !message) return;
        // Update the rail last_message preview in-place.
        const upd = (list) => list.map((c) => c.id === conversation_id ? {
          ...c,
          last_message_at: message.created_at,
          last_message_preview: message.body?.slice(0, 140) || "",
          last_sender_name: message.sender_name,
        } : c);
        if (conversation_kind === "channel") setChannels((cs) => upd(cs));
        else setDms((ds) => upd(ds));
      }
    };
    window.addEventListener("fable:event", onEvt);
    return () => window.removeEventListener("fable:event", onEvt);
  }, [loadRail]);

  const activeIsChannel = activeConv?.kind === "channel" || activeConv?.kind === "announcement";
  const activeIsAnnouncement = activeConv?.kind === "announcement";
  const activeIsDM      = activeConv?.kind === "dm";

  if (!moduleOn) {
    return (
      <div className="px-6 py-6 text-[13px] text-brand-muted" data-testid="comms-disabled">
        The Comms module is disabled for this company.
      </div>
    );
  }

  return (
    <div className="h-screen flex" data-testid="comms-page">
      {/* ── Left rail ───────────────────────────────────────────── */}
      <aside
        className={`${activeConv ? "hidden md:flex" : "flex"} w-full md:w-[260px] shrink-0 border-r border-brand-rule bg-card h-full flex-col`}
        data-testid="comms-rail"
      >
        <div className="px-4 py-3 border-b border-brand-rule">
          <div className="flex items-center gap-2">
            <ModuleMark id="comms" size={26} />
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-brand-muted">Module</div>
              <div className="text-[14.5px] font-bold text-brand-ink">Comms</div>
            </div>
            <button className="btn-ghost ml-auto !h-7 !px-2"
                    onClick={() => setSearchOpen(true)}
                    title="Search"
                    data-testid="comms-search-btn"
                    aria-label="Search comms">
              <Search size={13} />
            </button>
            <button className="btn-ghost !h-7 !px-2"
                    onClick={() => { setPinsTab("saved"); setPinsOpen(true); }}
                    title="Saved items"
                    data-testid="comms-saved-btn"
                    aria-label="Open saved items">
              <Bookmark size={13} />
            </button>
            <button className="btn-ghost !h-7 !px-2"
                    onClick={() => setKeywordOpen(true)}
                    title="Keyword alerts"
                    data-testid="comms-keyword-btn"
                    aria-label="Keyword alerts">
              <Bell size={13} />
            </button>
            <button className="btn-ghost !h-7 !px-2 xl:hidden"
                    onClick={() => setDirectoryOpen(true)}
                    title="On shift now"
                    data-testid="comms-directory-btn"
                    aria-label="Open directory">
              <Radio size={13} />
            </button>
            <button className="btn-ghost !h-7 !px-2"
                    onClick={() => setBroadcastsOpen(true)}
                    title="Broadcasts"
                    data-testid="comms-broadcasts-btn"
                    aria-label="Open broadcasts">
              <Megaphone size={13} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto py-2">
          {loading ? (
            <div className="px-3 space-y-2 pt-2" data-testid="comms-rail-loading">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-3 w-24 mt-3" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : (
          <>
          {/* Channels */}
          <div className="px-2 flex items-center justify-between">
            <div className="micro-label pl-2">Channels</div>
            {canManage && (
              <button
                type="button"
                onClick={() => setNewChannelOpen(true)}
                className="btn-ghost h-6 w-6 !p-0"
                data-testid="comms-new-channel"
                aria-label="New channel"
              >
                <Plus size={13} />
              </button>
            )}
          </div>
          <ChannelList
            kind="channel"
            items={channels}
            activeId={activeId}
            unreadByConv={byConv}
            onSelect={(c) => setParams({ open: c.id })}
            emptyLabel="No channels yet."
            testidPrefix="comms-channel"
          />

          {/* DMs */}
          <div className="mt-4 px-2 flex items-center justify-between">
            <div className="micro-label pl-2">Direct messages</div>
            {canPost && (
              <button
                type="button"
                onClick={() => setNewDMOpen(true)}
                className="btn-ghost h-6 w-6 !p-0"
                data-testid="comms-new-dm"
                aria-label="New DM"
              >
                <MessageSquarePlus size={13} />
              </button>
            )}
          </div>
          <ChannelList
            kind="dm"
            items={dms}
            activeId={activeId}
            unreadByConv={byConv}
            currentUserId={user?.id}
            onSelect={(c) => setParams({ open: c.id })}
            emptyLabel="No direct messages yet."
            testidPrefix="comms-dm"
          />
          </>
          )}
        </div>
      </aside>

      {/* ── Main pane ───────────────────────────────────────────── */}
      <section
        className={`${activeConv ? "flex" : "hidden md:flex"} flex-1 min-w-0 h-full flex-col`}
        data-testid="comms-main"
        onTouchStart={(e) => {
          // Track a right-swipe from near the left edge as "back to rail" on mobile.
          if (window.innerWidth >= 768) return;
          const t = e.touches[0];
          e.currentTarget.dataset.tsx = String(t.clientX);
          e.currentTarget.dataset.tsy = String(t.clientY);
        }}
        onTouchEnd={(e) => {
          if (window.innerWidth >= 768) return;
          const el = e.currentTarget;
          const startX = Number(el.dataset.tsx || 0);
          const startY = Number(el.dataset.tsy || 0);
          const endTouch = e.changedTouches[0];
          const dx = endTouch.clientX - startX;
          const dy = Math.abs(endTouch.clientY - startY);
          delete el.dataset.tsx; delete el.dataset.tsy;
          if (startX < 40 && dx > 80 && dy < 60) {
            // swipe-right from left edge → back to rail
            setParams({});
          }
        }}
      >
        {activeConv ? (
          <>
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-brand-rule">
              <div className="min-w-0 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setParams({})}
                  className="md:hidden btn-ghost h-8 w-8 !p-0"
                  aria-label="Back to channels"
                  data-testid="comms-back"
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center shrink-0">
                  {activeIsAnnouncement ? <Megaphone size={15} /> : activeIsChannel ? <Hash size={15} /> : <UserIcon size={15} />}
                </div>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-brand-ink truncate">
                    {activeIsChannel
                      ? <>#<span data-testid="comms-active-slug">{activeConv.slug}</span></>
                      : (activeConv.counterpart?.name || "Direct message")}
                  </div>
                  <div className="text-[11.5px] text-brand-muted truncate">
                    {activeIsChannel
                      ? (activeConv.description || "No description.")
                      : (activeConv.counterpart?.email || "")}
                    {activeIsChannel && activeConv.archived ? " · archived" : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {activeIsChannel && (
                  <button
                    type="button"
                    onClick={() => { setPinsTab("pins"); setPinsOpen(true); }}
                    className="btn-ghost h-8"
                    data-testid="comms-open-pins"
                    aria-label="Pinned messages"
                    title="Pinned messages"
                  >
                    <Pin size={13} />
                  </button>
                )}
                {activeIsChannel && (
                  <button
                    type="button"
                    onClick={() => setDetailOpen(true)}
                    className="btn-ghost h-8"
                    data-testid="comms-open-detail"
                  >
                    <Users size={13} /> Detail
                  </button>
                )}
              </div>
            </header>

            <MessageStream
              key={activeConv.id}
              companyId={companyId}
              conversation={activeConv}
              currentUserId={user?.id}
              canManage={canManage}
              onNewSeen={() => markRead(activeConv.id, null)}
              onOpenThread={(rootId) => { setThreadRootId(rootId); setThreadOpen(true); }}
              onQuoteReply={(msg) => setQuoteTarget(msg)}
            />

            <Composer
              companyId={companyId}
              conversation={activeConv}
              canManage={canManage}
              quoteTarget={quoteTarget}
              onClearQuote={() => setQuoteTarget(null)}
              disabled={!canPost || (activeIsChannel && activeConv.archived)
                          || (activeConv.kind === "announcement" && !canManage)}
              disabledReason={
                !canPost ? "You need comms.post to send messages."
                : activeConv.archived ? "This channel is archived."
                : (activeConv.kind === "announcement" && !canManage) ? "Only comms.manage can post here."
                : ""
              }
              onSent={(msg) => {
                // The composer's send path already produced the SSE event; the
                // rail last-message update happens via the same SSE listener.
                refreshUnread();
                setQuoteTarget(null);
              }}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center px-6"
               data-testid="comms-empty-state">
            <EmptyState
              icon={MessageSquare}
              eyebrow="Comms"
              title={channels.length === 0 && dms.length === 0 ? "No conversations yet." : "Select a channel or DM."}
              body={channels.length === 0 && dms.length === 0
                ? "Create your first channel or start a direct message with a teammate."
                : "Pick something from the left rail to jump in."}
              action={canManage && channels.length === 0
                ? <button className="btn-violet" onClick={() => setNewChannelOpen(true)}
                          data-testid="comms-empty-new-channel">
                    <Plus size={13}/> New channel
                  </button>
                : null}
            />
          </div>
        )}
      </section>

      {/* ── Right rail (≥ xl) — Live ops radar ──────────────────── */}
      {moduleOn && (
        <DirectoryRail
          companyId={companyId}
          canPost={canPost}
          onOpenConversation={(id) => setParams({ open: id })}
        />
      )}

      {/* Dialogs / drawers */}
      <NewChannelDialog
        open={newChannelOpen}
        onOpenChange={setNewChannelOpen}
        companyId={companyId}
        onCreated={(c) => { loadRail(); setParams({ open: c.id }); }}
      />
      <NewDMDialog
        open={newDMOpen}
        onOpenChange={setNewDMOpen}
        companyId={companyId}
        currentUserId={user?.id}
        onOpened={(c) => { loadRail(); setParams({ open: c.id }); }}
      />
      <ChannelDetailDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        companyId={companyId}
        channel={activeIsChannel ? activeConv : null}
        canManage={canManage}
        onChanged={(fresh) => {
          setActiveConv(fresh);
          loadRail();
        }}
      />
      <BroadcastsDrawer
        open={broadcastsOpen}
        onOpenChange={setBroadcastsOpen}
        companyId={companyId}
        sites={sites}
        canManage={canManage}
      />
      <DirectoryDrawer
        open={directoryOpen}
        onOpenChange={setDirectoryOpen}
        companyId={companyId}
        canPost={canPost}
        onOpenConversation={(id) => setParams({ open: id })}
      />
      {activeConv && (
        <ThreadDrawer
          open={threadOpen}
          onOpenChange={setThreadOpen}
          companyId={companyId}
          conversation={activeConv}
          rootId={threadRootId}
        />
      )}
      {activeConv && (
        <PinsSavedDrawer
          open={pinsOpen}
          onOpenChange={setPinsOpen}
          initialTab={pinsTab}
          companyId={companyId}
          conversation={activeConv}
          onJump={(id) => setParams({ open: id })}
        />
      )}
      <SearchDrawer
        open={searchOpen}
        onOpenChange={setSearchOpen}
        companyId={companyId}
        onJump={(convId) => setParams({ open: convId })}
      />
      <KeywordAlertsDialog
        open={keywordOpen}
        onOpenChange={setKeywordOpen}
        companyId={companyId}
      />
    </div>
  );
}
